import React, { useEffect, useState } from "react";
import { Button, Card, Col, Row, Spinner, Alert, Table, Badge, Tabs, Tab, ProgressBar, Modal } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { formatCurrency, formatNumber, formatDate } from "../utils/formatters";
import './CommandesCours.css';

export default function CommandesCours() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('clients');
  const [selectedCommande, setSelectedCommande] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailType, setDetailType] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);

  useEffect(() => {
    fetchCommandesCours();
    
    let interval;
    if (autoRefresh) {
      interval = setInterval(fetchCommandesCours, 60000);
    }
    return () => clearInterval(interval);
  }, [autoRefresh]);

  const fetchCommandesCours = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      
      const response = await fetch(
        `https://sawd.julesferry-ga.com/api/commandes_cours.php`,
        {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem("token");
          localStorage.removeItem("user");
          navigate("/pages/signin2");
          throw new Error("Session expirée");
        }
        throw new Error(`Erreur HTTP: ${response.status}`);
      }
      
      const result = await response.json();
      setData(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (type, id) => {
    try {
      setLoadingDetails(true);
      const token = localStorage.getItem("token");
      const action = type === 'fournisseur' ? 'detail_fournisseur' : 'detail_client';
      
      const response = await fetch(
        `https://sawd.julesferry-ga.com/api/commandes_cours.php?action=${action}&id=${id}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      const result = await response.json();
      
      // Vérifier la structure de la réponse et adapter
      console.log("Détails reçus:", result);
      
      // Si result.commande existe, on l'utilise, sinon on utilise result directement
      const commandeDetails = result.commande || result;
      
      // S'assurer que c'est un tableau
      setSelectedCommande(Array.isArray(commandeDetails) ? commandeDetails : [commandeDetails]);
      setDetailType(type);
      setShowDetailModal(true);
    } catch (err) {
      console.error("Erreur chargement détails:", err);
      alert("Erreur lors du chargement des détails");
    } finally {
      setLoadingDetails(false);
    }
  };

  const getStatutBadge = (statut) => {
    const badges = {
      'emise': { bg: 'warning', text: 'À encaisser' },
      'payee_partielle': { bg: 'info', text: 'Partiellement payée' },
      'envoyee': { bg: 'primary', text: 'Envoyée' },
      'recue_partielle': { bg: 'info', text: 'Réception partielle' },
      'complete': { bg: 'success', text: 'Complète' },
      'partielle': { bg: 'info', text: 'Partielle' },
      'en_attente': { bg: 'secondary', text: 'En attente' }
    };
    return badges[statut] || { bg: 'secondary', text: statut };
  };

  const getPrioriteBadge = (priorite) => {
    const badges = {
      'expiree': { bg: 'danger', text: 'Expirée', icon: 'ri-alert-line' },
      'urgente': { bg: 'warning', text: 'Urgente', icon: 'ri-alarm-warning-line' },
      'normale': { bg: 'success', text: 'Normale', icon: 'ri-check-line' }
    };
    return badges[priorite] || badges.normale;
  };

  // Bouton d'action stylisé
  const ActionButton = ({ onClick, tooltip }) => (
    <Button
      variant="light"
      size="sm"
      onClick={onClick}
      className="action-btn-light"
      title={tooltip}
      style={{
        width: '32px',
        height: '32px',
        borderRadius: '8px',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#f8f9fa',
        border: '1px solid #e9ecef',
        color: '#495057',
        transition: 'all 0.2s'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = '#e9ecef';
        e.currentTarget.style.color = '#0d6efd';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = '#f8f9fa';
        e.currentTarget.style.color = '#495057';
      }}
    >
      <i className="ri-eye-line"></i>
    </Button>
  );

  if (loading && !data) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <Spinner animation="border" variant="primary" />
        <span className="ms-3">Chargement des commandes en cours...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4">
        <Alert variant="danger">
          <Alert.Heading>Erreur</Alert.Heading>
          <p>{error}</p>
          <Button variant="outline-danger" onClick={fetchCommandesCours}>
            Réessayer
          </Button>
        </Alert>
      </div>
    );
  }

  return (
    <React.Fragment>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        {/* En-tête */}
        <div className="d-md-flex align-items-center justify-content-between mb-4">
          <div>
            <ol className="breadcrumb fs-sm mb-1">
              <li className="breadcrumb-item"><Link to="/dashboard">Tableau de bord</Link></li>
              <li className="breadcrumb-item active">Commandes en cours</li>
            </ol>
            <h4 className="main-title mb-0">Suivi des commandes</h4>
          </div>
          <div className="d-flex gap-2 mt-3 mt-md-0">
            <div className="form-check form-switch me-3 d-flex align-items-center">
              <input
                className="form-check-input"
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                id="autoRefresh"
              /> 
              <label className="form-check-label small ms-2" htmlFor="autoRefresh">
                Auto-refresh
              </label>
            </div>
            <Button variant="outline-primary" onClick={fetchCommandesCours} size="sm">
              <i className="ri-refresh-line"></i> Actualiser
            </Button>
          </div>
        </div>

        {/* KPI Cards */}
        <Row className="g-3 mb-4">
          <Col sm="6" xl="3">
            <Card className="card-one h-100 border-0 shadow-sm">
              <Card.Body>
                <div className="d-flex align-items-center">
                  <div className="kpi-icon bg-primary-soft rounded-circle p-3 me-3">
                    <i className="ri-shopping-cart-line fs-24 text-primary"></i>
                  </div>
                  <div>
                    <h6 className="text-secondary mb-1">Commandes fournisseurs</h6>
                    <h3 className="mb-0 fw-bold">{data?.statistiques?.nb_commandes_fournisseurs || 0}</h3>
                    <small className="text-secondary">
                      {formatCurrency(data?.statistiques?.valeur_commandes_fournisseurs || 0)}
                    </small>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col sm="6" xl="3">
            <Card className="card-one h-100 border-0 shadow-sm">
              <Card.Body>
                <div className="d-flex align-items-center">
                  <div className="kpi-icon bg-success-soft rounded-circle p-3 me-3">
                    <i className="ri-file-list-3-line fs-24 text-success"></i>
                  </div>
                  <div>
                    <h6 className="text-secondary mb-1">Commandes clients</h6>
                    <h3 className="mb-0 fw-bold">{data?.statistiques?.nb_commandes_clients || 0}</h3>
                    <small className="text-secondary">
                      {formatCurrency(data?.statistiques?.valeur_commandes_clients || 0)}
                    </small>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col sm="6" xl="3">
            <Card className="card-one h-100 border-0 shadow-sm border-start border-danger border-4">
              <Card.Body>
                <div className="d-flex align-items-center">
                  <div className="kpi-icon bg-danger-soft rounded-circle p-3 me-3">
                    <i className="ri-error-warning-line fs-24 text-danger"></i>
                  </div>
                  <div>
                    <h6 className="text-secondary mb-1">Factures expirées</h6>
                    <h3 className="mb-0 fw-bold text-danger">{data?.statistiques?.nb_factures_expirees || 0}</h3>
                    <small className="text-danger">
                      {formatCurrency(data?.statistiques?.montant_factures_expirees || 0)}
                    </small>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col sm="6" xl="3">
            <Card className="card-one h-100 border-0 shadow-sm border-start border-warning border-4">
              <Card.Body>
                <div className="d-flex align-items-center">
                  <div className="kpi-icon bg-warning-soft rounded-circle p-3 me-3">
                    <i className="ri-truck-line fs-24 text-warning"></i>
                  </div>
                  <div>
                    <h6 className="text-secondary mb-1">Réceptions en attente</h6>
                    <h3 className="mb-0 fw-bold text-warning">{data?.receptions?.length || 0}</h3>
                    <small className="text-warning">commandes partielles</small>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Onglets principaux */}
        <Card className="border-0 shadow-sm">
          <Card.Header className="bg-transparent border-0">
            <Tabs
              activeKey={activeTab}
              onSelect={(k) => setActiveTab(k)}
              className="mb-3"
            >
              <Tab 
                eventKey="clients" 
                title={
                  <span>
                    <i className="ri-user-line me-2"></i>
                    Commandes clients ({data?.commandes_clients?.length || 0})
                  </span>
                }
              >
                <div className="table-responsive">
                  <Table hover className="align-middle">
                    <thead className="table-light">
                      <tr>
                        <th>N° Facture</th>
                        <th>Date</th>
                        <th>Client</th>
                        <th>Échéance</th>
                        <th className="text-end">Montant</th>
                        <th className="text-end">Réglé</th>
                        <th className="text-end">Reste</th>
                        <th>Priorité</th>
                        <th>Statut</th>
                        <th className="text-center">Détails</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data?.commandes_clients?.map((commande) => {
                        const priorite = getPrioriteBadge(commande.priorite);
                        const statut = getStatutBadge(commande.statut);
                        const reste = commande.montant_ttc - commande.montant_regle;
                        
                        return (
                          <tr key={commande.id_facture} className={commande.priorite === 'expiree' ? 'table-danger' : ''}>
                            <td>
                              <Link to={`/ventes/facturesDetails/${commande.id_facture}`} className="fw-semibold">
                                {commande.numero_facture}
                              </Link>
                            </td>
                            <td>{formatDate(commande.date_facture)}</td>
                            <td>
                              <div>
                                <span className="d-block fw-semibold">{commande.client}</span>
                                <small className="text-secondary">{commande.code_client}</small>
                              </div>
                            </td>
                            <td>
                              <div>
                                <span className="d-block">{formatDate(commande.date_echeance)}</span>
                                <small className={`text-${commande.jours_restants < 0 ? 'danger' : 'secondary'}`}>
                                  {commande.jours_restants < 0 
                                    ? `Retard de ${Math.abs(commande.jours_restants)}j`
                                    : `Dans ${commande.jours_restants}j`
                                  }
                                </small>
                              </div>
                            </td>
                            <td className="text-end">{formatCurrency(commande.montant_ttc)}</td>
                            <td className="text-end text-success">{formatCurrency(commande.montant_regle)}</td>
                            <td className="text-end text-danger fw-bold">{formatCurrency(reste)}</td>
                            <td>
                              <Badge bg={priorite.bg} className="d-flex align-items-center gap-1" style={{ width: 'fit-content' }}>
                                <i className={priorite.icon}></i>
                                {priorite.text}
                              </Badge>
                            </td>
                            <td>
                              <Badge bg={statut.bg}>{statut.text}</Badge>
                            </td>
                            <td className="text-center">
                              <ActionButton
                                onClick={() => handleViewDetails('client', commande.id_facture)}
                                tooltip="Voir les détails de la commande"
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
              </Tab>

              <Tab 
                eventKey="fournisseurs" 
                title={
                  <span>
                    <i className="ri-truck-line me-2"></i>
                    Commandes fournisseurs ({data?.commandes_fournisseurs?.length || 0})
                  </span>
                }
              >
                <div className="table-responsive">
                  <Table hover className="align-middle">
                    <thead className="table-light">
                      <tr>
                        <th>N° Commande</th>
                        <th>Date</th>
                        <th>Fournisseur</th>
                        <th>Livraison prévue</th>
                        <th className="text-end">Montant</th>
                        <th>Réception</th>
                        <th>Statut</th>
                        <th className="text-center">Détails</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data?.commandes_fournisseurs?.map((commande) => {
                        const statut = getStatutBadge(commande.statut);
                        const etatReception = getStatutBadge(commande.etat_reception);
                        const progress = (commande.quantite_recue / commande.quantite_totale) * 100;
                        
                        return (
                          <tr key={commande.id_commande_fournisseur}>
                            <td>
                              <Link to={`/achats/commandes/${commande.id_commande_fournisseur}`} className="fw-semibold">
                                {commande.numero_commande}
                              </Link>
                            </td>
                            <td>{formatDate(commande.date_commande)}</td>
                            <td>
                              <div>
                                <span className="d-block fw-semibold">{commande.fournisseur}</span>
                                <small className="text-secondary">{commande.code_fournisseur}</small>
                              </div>
                            </td>
                            <td>
                              <div>
                                <span className="d-block">{formatDate(commande.date_livraison_prevue)}</span>
                                {commande.date_livraison_prevue < new Date().toISOString().split('T')[0] && (
                                  <small className="text-danger">En retard</small>
                                )}
                              </div>
                            </td>
                            <td className="text-end">{formatCurrency(commande.montant_ttc)}</td>
                            <td style={{ minWidth: '120px' }}>
                              <div className="d-flex align-items-center gap-2">
                                <ProgressBar now={progress} style={{ width: '60px', height: '6px' }} />
                                <small>
                                  {commande.quantite_recue}/{commande.quantite_totale}
                                </small>
                              </div>
                              <Badge bg={etatReception.bg} size="sm">
                                {etatReception.text}
                              </Badge>
                            </td>
                            <td>
                              <Badge bg={statut.bg}>{statut.text}</Badge>
                            </td>
                            <td className="text-center">
                              <ActionButton
                                onClick={() => handleViewDetails('fournisseur', commande.id_commande_fournisseur)}
                                tooltip="Voir les détails de la commande"
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
              </Tab>

              <Tab 
                eventKey="echeances" 
                title={
                  <span>
                    <i className="ri-calendar-check-line me-2"></i>
                    Échéances ({data?.echeances?.length || 0})
                  </span>
                }
              >
                <div className="table-responsive">
                  <Table hover className="align-middle">
                    <thead className="table-light">
                      <tr>
                        <th>Facture</th>
                        <th>Client</th>
                        <th>Date facture</th>
                        <th>Date échéance</th>
                        <th className="text-end">Montant dû</th>
                        <th>Jours restants</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data?.echeances?.map((echeance) => (
                        <tr key={echeance.id_facture}>
                          <td className="fw-semibold">{echeance.numero_facture}</td>
                          <td>{echeance.client}</td>
                          <td>{formatDate(echeance.date_facture)}</td>
                          <td className={echeance.jours_restants < 0 ? 'text-danger fw-bold' : ''}>
                            {formatDate(echeance.date_echeance)}
                          </td>
                          <td className="text-end fw-bold text-primary">
                            {formatCurrency(echeance.montant_du)}
                          </td>
                          <td>
                            <Badge bg={echeance.jours_restants < 0 ? 'danger' : echeance.jours_restants <= 3 ? 'warning' : 'success'}>
                              {echeance.jours_restants < 0 
                                ? `Retard ${Math.abs(echeance.jours_restants)}j`
                                : `J-${echeance.jours_restants}`
                              }
                            </Badge>
                          </td>
                          <td>
                            <Button onClick={() => navigate(`/ventes/facturesDetails/${echeance.id_facture}`)} size="sm" variant="outline-primary">
                              <i className="ri-bank-card-line"></i> Encaisser
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </Tab>

              <Tab 
                eventKey="receptions" 
                title={
                  <span>
                    <i className="ri-inbox-archive-line me-2"></i>
                    Réceptions attendues ({data?.receptions?.length || 0})
                  </span>
                }
              >
                <div className="table-responsive">
                  <Table hover className="align-middle">
                    <thead className="table-light">
                      <tr>
                        <th>Commande</th>
                        <th>Fournisseur</th>
                        <th>Livraison prévue</th>
                        <th className="text-end">Produits</th>
                        <th className="text-end">Quantité restante</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data?.receptions?.map((reception) => (
                        <tr key={reception.id_commande_fournisseur}>
                          <td className="fw-semibold">{reception.numero_commande}</td>
                          <td>{reception.fournisseur}</td>
                          <td className={reception.date_livraison_prevue < new Date().toISOString().split('T')[0] ? 'text-danger' : ''}>
                            {formatDate(reception.date_livraison_prevue)}
                          </td>
                          <td className="text-end">{reception.nb_produits}</td>
                          <td className="text-end fw-bold text-warning">{reception.quantite_restante}</td>
                          <td>
                            <Button onClick={() => navigate(`/achats/commandes/${reception.id_commande_fournisseur}`)} size="sm" variant="outline-success">
                              <i className="ri-check-line"></i> Réceptionner
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </Tab>
            </Tabs>
          </Card.Header>
        </Card>

        <Footer />
      </div>

      {/* Modal de détail amélioré */}
      <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)} size="lg" centered>
        <Modal.Header closeButton className="bg-light">
          <Modal.Title>
            <i className={`ri-${detailType === 'fournisseur' ? 'truck' : 'user'}-line me-2 text-primary`}></i>
            {detailType === 'fournisseur' ? 'Détail commande fournisseur' : 'Détail commande client'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingDetails ? (
            <div className="text-center py-4">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2">Chargement des détails...</p>
            </div>
          ) : selectedCommande && selectedCommande.length > 0 ? (
            <>
           
              <div className="mb-3 p-3 bg-light rounded">
                <h6 className="mb-3 text-primary">Récapitulatif de la commande</h6>
                <Row>
                  <Col md={6}>
                    <p className="mb-1"><strong>Total articles:</strong> {selectedCommande.length}</p>
                    <p className="mb-1"><strong>Quantité totale:</strong> {
                      selectedCommande.reduce((acc, item) => acc + (item.quantite || item.quantite_commandee || 0), 0)
                    }</p>
                  </Col>
                  <Col md={6}>
                    <p className="mb-1"><strong>Total HT:</strong> {
                      formatCurrency(selectedCommande.reduce((acc, item) => acc + (item.montant_ligne_ht || 0), 0))
                    }</p>
                  </Col>
                </Row>
              </div>

              <div className="table-responsive">
                <Table striped hover className="align-middle mb-0">
                  <thead className="table-primary">
                    <tr>
                      <th>Référence</th>
                      <th>Produit</th>
                      <th className="text-center">Quantité</th>
                      <th className="text-end">Prix unitaire</th>
                      <th className="text-end">Total HT</th>
                      {detailType === 'fournisseur' && <th className="text-center">Reçu</th>}
                      {detailType === 'client' && <th className="text-center">Stock</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {selectedCommande.map((ligne, idx) => (
                      <tr key={idx}>
                        <td>
                          <Badge bg="light" text="dark" className="font-mono">
                            {ligne.reference}
                          </Badge>
                        </td>
                        <td className="fw-semibold">{ligne.designation}</td>
                        <td className="text-center">{ligne.quantite || ligne.quantite_commandee}</td>
                        <td className="text-end">{formatCurrency(ligne.prix_unitaire_ht)}</td>
                        <td className="text-end text-primary fw-bold">{formatCurrency(ligne.montant_ligne_ht)}</td>
                        {detailType === 'fournisseur' && (
                          <td className="text-center">
                            <Badge 
                              bg={ligne.quantite_recue >= (ligne.quantite_commandee || ligne.quantite) ? 'success' : 'warning'}
                              className="px-3 py-2"
                            >
                              <i className={`ri-${ligne.quantite_recue >= (ligne.quantite_commandee || ligne.quantite) ? 'checkbox-circle' : 'time'}-line me-1`}></i>
                              {ligne.quantite_recue || 0}/{ligne.quantite_commandee || ligne.quantite}
                            </Badge>
                          </td>
                        )}
                        {detailType === 'client' && (
                          <td className="text-center">
                            <Badge 
                              bg={ligne.etat_stock === 'disponible' ? 'success' : 'danger'}
                              className="px-3 py-2"
                            >
                              <i className={`ri-${ligne.etat_stock === 'disponible' ? 'check' : 'close'}-circle-line me-1`}></i>
                              {ligne.etat_stock === 'disponible' ? 'Disponible' : 'Stock insuffisant'}
                            </Badge>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </>
          ) : (
            <p className="text-center py-4 text-muted">Aucun détail disponible</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDetailModal(false)}>
            Fermer
          </Button>
          
          {/* <Button onClick={() => navigate(`/ventes/facturesDetails/${selectedCommande.reduce((acc, item) => acc + (item), 0)}`)} variant="primary" className="px-4">
            <i className={`ri-${detailType === 'fournisseur' ? 'inbox-archive' : 'bank-card'}-line me-2`}></i>
            {detailType === 'fournisseur' ? 'Réceptionner' : 'Encaisser'}
          </Button> */}
        </Modal.Footer>
      </Modal>
    </React.Fragment>        
  );
}  